import { useState, useEffect } from "react"
import { useAuthStore } from "../stores/authStore"
import {
  Users, UserPlus, Edit2, Trash2, Key, Shield, CheckCircle, XCircle,
  Search, ChevronDown, ChevronUp, Lock, Eye, EyeOff, RefreshCw, X,
  Mail, Phone, UserCheck, UserX, Filter
} from "lucide-react"
import axios from "axios"
import "./Administration.css"

const API_URL = import.meta.env.VITE_API_URL || "https://www.secs.gismac.co.ke/api"

const ALL_PERMISSIONS = [
  { key: "users.manage", label: "User Management", category: "Administration" },
  { key: "guards.manage", label: "Guard Operations", category: "Operations" },
  { key: "shifts.manage", label: "Shift Scheduling", category: "Operations" },
  { key: "incidents.manage", label: "Incident Reports", category: "Operations" },
  { key: "personnel.manage", label: "Personnel Management", category: "HR" },
  { key: "leave.manage", label: "Leave Management", category: "HR" },
  { key: "finance.manage", label: "Finance & Budget", category: "Finance" },
  { key: "assets.manage", label: "Assets & Equipment", category: "Finance" },
  { key: "sites.manage", label: "Site Management", category: "Operations" },
  { key: "reports.view", label: "View Reports", category: "Reports" },
  { key: "reports.create", label: "Create Reports", category: "Reports" },
  { key: "supply_chain.manage", label: "Supply Chain", category: "Operations" },
]

// TO:
const ROLE_OPTIONS = [
  { value: "admin", label: "Administrator", color: "#dc2626" },
  { value: "hr", label: "HR Manager", color: "#2563eb" },
  { value: "supervisor", label: "Supervisor", color: "#059669" },
  { value: "finance", label: "Finance Officer", color: "#7c3aed" },
  { value: "assistant_hr", label: "Assistant HR", color: "#ea580c" },
]




const ROLE_BADGES = {
  admin: { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
  hr: { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  supervisor: { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" },
  finance: { bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" },
  assistant_hr: { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
}
const DEFAULT_BADGE = { bg: "#f3f4f6", color: "#374151", border: "#d1d5db" }

export default function Administration() {
  const { user: currentUser, token } = useAuthStore()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [roleFilter, setRoleFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" })
  const [message, setMessage] = useState({ text: "", type: "" })

  const [showUserModal, setShowUserModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [modalMode, setModalMode] = useState("create")

  const [formData, setFormData] = useState({
    firstName: "", lastName: "", email: "", password: "", confirmPassword: "",
    role: "assistant_hr", phone: "", isActive: true, permissions: [],
  })
  const [showPassword, setShowPassword] = useState(false)
  const [resetPassword, setResetPassword] = useState("")
  const [formErrors, setFormErrors] = useState({})

  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } }

  useEffect(() => { fetchUsers() }, [])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${API_URL}/users`, axiosConfig)
      setUsers(res.data.data || [])
    } catch (err) {
      showMessage(err.response?.data?.message || "Failed to load users", "error")
    } finally { setLoading(false) }
  }

  const showMessage = (text, type = "success") => {
    setMessage({ text, type })
    setTimeout(() => setMessage({ text: "", type: "" }), 4000)
  }

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }))
  }

  const filteredUsers = users
    .filter((u) => {
      const matchesSearch = !searchTerm ||
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesRole = roleFilter === "all" || u.role === roleFilter
      const matchesStatus = statusFilter === "all" || (statusFilter === "active" ? u.isActive : !u.isActive)
      return matchesSearch && matchesRole && matchesStatus
    })
    .sort((a, b) => {
      const aVal = a[sortConfig.key] || ""
      const bVal = b[sortConfig.key] || ""
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1
      return 0
    })

  const openCreateModal = () => {
    setSelectedUser(null)
    setModalMode("create")
    setFormData({
      firstName: "", lastName: "", email: "", password: "", confirmPassword: "",
      role: "assistant_hr", phone: "", isActive: true,
      permissions: getDefaultPermissions("assistant_hr"),
    })
    setFormErrors({})
    setShowUserModal(true)
  }

  const openEditModal = (user) => {
    setSelectedUser(user)
    setModalMode("edit")
    setFormData({
      firstName: user.firstName || "", lastName: user.lastName || "", email: user.email || "",
      password: "", confirmPassword: "", role: user.role || "assistant_hr",
      phone: user.phone || "", isActive: user.isActive !== undefined ? user.isActive : true,
      permissions: user.permissions || [],
    })
    setFormErrors({})
    setShowUserModal(true)
  }

  const openPasswordModal = (user) => { setSelectedUser(user); setResetPassword(""); setShowPasswordModal(true) }
  const openDeleteModal = (user) => { setSelectedUser(user); setShowDeleteModal(true) }
  const openPermissionsModal = (user) => {
    setSelectedUser(user)
    setFormData((prev) => ({ ...prev, permissions: user.permissions || [] }))
    setShowPermissionsModal(true)
  }

  const getDefaultPermissions = (role) => {
    switch (role) {
      case "admin": return ALL_PERMISSIONS.map((p) => p.key)
      case "hr": return ["personnel.manage", "leave.manage", "guards.manage", "reports.view", "reports.create", "shifts.manage", "incidents.manage", "finance.manage", "assets.manage", "sites.manage", "supply_chain.manage"]
      case "supervisor": return ["guards.manage", "shifts.manage", "incidents.manage", "sites.manage", "reports.view", "reports.create", "personnel.manage", "leave.manage", "finance.manage", "assets.manage", "supply_chain.manage"]
      case "finance": return ["finance.manage", "assets.manage", "reports.view", "reports.create", "personnel.manage", "leave.manage", "guards.manage", "shifts.manage", "incidents.manage", "sites.manage", "supply_chain.manage"]
      case "assistant_hr": return ["personnel.manage", "leave.manage", "reports.view", "guards.manage", "shifts.manage", "incidents.manage", "finance.manage", "assets.manage", "sites.manage", "reports.create", "supply_chain.manage"]
      default: return ["reports.view"]
    }
  }

  const handleRoleChange = (role) => {
    setFormData((prev) => ({ ...prev, role }))
  }

  const togglePermission = (permKey) => {
    setFormData((prev) => {
      const perms = prev.permissions || []
      if (perms.includes(permKey)) return { ...prev, permissions: perms.filter((p) => p !== permKey) }
      return { ...prev, permissions: [...perms, permKey] }
    })
  }

  const validateForm = () => {
    const errors = {}
    if (!formData.firstName?.trim()) errors.firstName = "First name is required"
    if (!formData.lastName?.trim()) errors.lastName = "Last name is required"
    if (!formData.email?.trim()) errors.email = "Email is required"
    else if (!/^\S+@\S+\.\S+$/.test(formData.email)) errors.email = "Invalid email format"
    if (modalMode === "create") {
      if (!formData.password) errors.password = "Password is required"
      else if (formData.password.length < 6) errors.password = "Password must be at least 6 characters"
      if (formData.password !== formData.confirmPassword) errors.confirmPassword = "Passwords do not match"
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return
    setLoading(true)
    try {
      const payload = {
        firstName: formData.firstName, lastName: formData.lastName, email: formData.email,
        role: formData.role, phone: formData.phone, isActive: formData.isActive,
        permissions: formData.permissions,
      }
      if (modalMode === "create") {
        payload.password = formData.password
        await axios.post(`${API_URL}/users`, payload, axiosConfig)
        showMessage("User created successfully")
      } else {
        await axios.put(`${API_URL}/users/${selectedUser._id}`, payload, axiosConfig)
        showMessage("User updated successfully")
      }
      setShowUserModal(false)
      fetchUsers()
    } catch (err) {
      showMessage(err.response?.data?.message || "Operation failed", "error")
    } finally { setLoading(false) }
  }

  const handleResetPassword = async (e) => {
    e.preventDefault()
    if (!resetPassword || resetPassword.length < 6) {
      showMessage("Password must be at least 6 characters", "error")
      return
    }
    setLoading(true)
    try {
      await axios.put(`${API_URL}/users/${selectedUser._id}/reset-password`, { newPassword: resetPassword }, axiosConfig)
      showMessage("Password reset successfully")
      setShowPasswordModal(false)
    } catch (err) {
      showMessage(err.response?.data?.message || "Failed to reset password", "error")
    } finally { setLoading(false) }
  }

  const handleDelete = async () => {
    setLoading(true)
    try {
      await axios.delete(`${API_URL}/users/${selectedUser._id}`, axiosConfig)
      showMessage("User deleted successfully")
      setShowDeleteModal(false)
      fetchUsers()
    } catch (err) {
      showMessage(err.response?.data?.message || "Failed to delete user", "error")
    } finally { setLoading(false) }
  }

  const handleToggleStatus = async (user) => {
    setLoading(true)
    try {
      await axios.put(`${API_URL}/users/${user._id}/toggle-status`, {}, axiosConfig)
      showMessage(`User ${user.isActive ? "deactivated" : "activated"} successfully`)
      fetchUsers()
    } catch (err) {
      showMessage(err.response?.data?.message || "Failed to toggle status", "error")
    } finally { setLoading(false) }
  }

  const handleSavePermissions = async () => {
    setLoading(true)
    try {
      await axios.put(`${API_URL}/users/${selectedUser._id}`, { permissions: formData.permissions }, axiosConfig)
      showMessage("Permissions updated successfully")
      setShowPermissionsModal(false)
      fetchUsers()
    } catch (err) {
      showMessage(err.response?.data?.message || "Failed to update permissions", "error")
    } finally { setLoading(false) }
  }

  const getRoleLabel = (role) => ROLE_OPTIONS.find((r) => r.value === role)?.label || role
  const getRoleStyle = (role) => ROLE_BADGES[role] || DEFAULT_BADGE
  const getPermissionLabel = (key) => ALL_PERMISSIONS.find((p) => p.key === key)?.label || key
  const getPermissionCategory = (key) => ALL_PERMISSIONS.find((p) => p.key === key)?.category || "Other"
  const permissionCategories = [...new Set(ALL_PERMISSIONS.map((p) => p.category))]

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div className="header-left">
          <Shield className="header-icon" size={28} />
          <div><h1>User Administration</h1><p>Manage users, roles, permissions, and access control</p></div>
        </div>
        <button className="btn btn-primary btn-create" onClick={openCreateModal} disabled={loading}>
          <UserPlus size={18} /> Add New User
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><Users size={24} /><div><h3>{users.length}</h3><p>Total Users</p></div></div>
        <div className="stat-card active"><UserCheck size={24} /><div><h3>{users.filter((u) => u.isActive).length}</h3><p>Active Users</p></div></div>
        <div className="stat-card inactive"><UserX size={24} /><div><h3>{users.filter((u) => !u.isActive).length}</h3><p>Inactive Users</p></div></div>
        <div className="stat-card admin"><Shield size={24} /><div><h3>{users.filter((u) => u.role === "admin").length}</h3><p>Administrators</p></div></div>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.type === "success" ? <CheckCircle size={18} /> : <XCircle size={18} />}{message.text}
        </div>
      )}

      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input type="text" placeholder="Search by name, email, or phone..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="filter-group">
          <Filter size={16} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option><option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("firstName")}>User {sortConfig.key === "firstName" && (sortConfig.direction === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
              <th onClick={() => handleSort("email")}>Email {sortConfig.key === "email" && (sortConfig.direction === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
              <th onClick={() => handleSort("role")}>Role {sortConfig.key === "role" && (sortConfig.direction === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
              <th>Permissions</th>
              <th onClick={() => handleSort("isActive")}>Status {sortConfig.key === "isActive" && (sortConfig.direction === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
              <th onClick={() => handleSort("lastLogin")}>Last Login {sortConfig.key === "lastLogin" && (sortConfig.direction === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 ? (
              <tr><td colSpan={7} className="loading-cell"><RefreshCw className="spin" size={24} /> Loading users...</td></tr>
            ) : filteredUsers.length === 0 ? (
              <tr><td colSpan={7} className="empty-cell"><Users size={48} /><p>No users found</p></td></tr>
            ) : (filteredUsers.map((u) => {
              const roleStyle = getRoleStyle(u.role)
              return (
                <tr key={u._id} className={!u.isActive ? "row-inactive" : ""}>
                  <td>
                    <div className="user-cell">
                      <div className="user-avatar">{u.firstName?.[0]}{u.lastName?.[0]}</div>
                      <div>
                        <div className="user-name">{u.firstName} {u.lastName}</div>
                        {u.phone && <div className="user-phone"><Phone size={12} /> {u.phone}</div>}
                      </div>
                    </div>
                  </td>
                  <td><div className="email-cell"><Mail size={14} />{u.email}</div></td>
                  <td><span className="role-badge" style={{ background: roleStyle.bg, color: roleStyle.color, border: `1px solid ${roleStyle.border}` }}>{getRoleLabel(u.role)}</span></td>
                  <td>
                    <div className="permissions-cell">
                      <span className="perm-count">{(u.permissions || []).length} permissions</span>
                      <button className="btn-icon btn-perm" onClick={() => openPermissionsModal(u)} title="Manage Permissions"><Shield size={14} /></button>
                    </div>
                  </td>
                  <td>
                    <button className={`status-toggle ${u.isActive ? "active" : "inactive"}`} onClick={() => handleToggleStatus(u)} disabled={loading}>
                      {u.isActive ? <><CheckCircle size={14} /> Active</> : <><XCircle size={14} /> Inactive</>}
                    </button>
                  </td>
                  <td><span className="last-login">{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : "Never"}</span></td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon btn-edit" onClick={() => openEditModal(u)} title="Edit User"><Edit2 size={16} /></button>
                      <button className="btn-icon btn-password" onClick={() => openPasswordModal(u)} title="Reset Password"><Key size={16} /></button>
                      <button className="btn-icon btn-delete" onClick={() => openDeleteModal(u)} title="Delete User" disabled={u._id === currentUser?.id}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              )
            }))}
          </tbody>
        </table>
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowUserModal(false)}>
          <div className="modal-content modal-lg">
            <div className="modal-header">
              <h3>{modalMode === "create" ? "Add New User" : "Edit User"}</h3>
              <button className="btn-close" onClick={() => setShowUserModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-section">
                  <h4>Basic Information</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input type="text" value={formData.firstName} onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))} className={formErrors.firstName ? "error" : ""} />
                      {formErrors.firstName && <span className="error-text">{formErrors.firstName}</span>}
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input type="text" value={formData.lastName} onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))} className={formErrors.lastName ? "error" : ""} />
                      {formErrors.lastName && <span className="error-text">{formErrors.lastName}</span>}
                    </div>
                    <div className="form-group">
                      <label>Email *</label>
                      <input type="email" value={formData.email} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} className={formErrors.email ? "error" : ""} />
                      {formErrors.email && <span className="error-text">{formErrors.email}</span>}
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input type="text" value={formData.phone} onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))} placeholder="+254..." />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Role & Access</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Role *</label>
                                            <input
                        type="text"
                        name="role"
                        value={formData.role || ""}
                        onChange={(e) => handleRoleChange(e.target.value)}
                        placeholder="Enter role (e.g., assistant_manager)"
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select value={formData.isActive ? "true" : "false"} onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.value === "true" }))}>
                        <option value="true">Active</option><option value="false">Inactive</option>
                      </select>
                    </div>
                  </div>
                                    <div className="role-description">
                    <Shield size={16} />
                    <span>
                      {formData.role === "admin" && "Full system access - can manage all users and settings"}
                      {formData.role === "hr" && "Manage personnel, leave, guards, and reports"}
                      {formData.role === "supervisor" && "Manage guards, shifts, incidents, sites, and reports"}
                      {formData.role === "finance" && "Manage finance, assets, and reports"}
                      {formData.role === "assistant_hr" && "View personnel, leave, and reports (read-only mostly)"}
                      {formData.role && !["admin", "hr", "supervisor", "finance", "assistant_hr"].includes(formData.role) && `Custom role: ${formData.role} - Assign permissions below to define access`}
                    </span>
                    </div>        
                </div>

                {modalMode === "create" && (
                  <div className="form-section">
                    <h4>Password</h4>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Password *</label>
                        <div className="password-input">
                          <input type={showPassword ? "text" : "password"} value={formData.password} onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))} className={formErrors.password ? "error" : ""} />
                          <button type="button" className="btn-eye" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                        </div>
                        {formErrors.password && <span className="error-text">{formErrors.password}</span>}
                      </div>
                      <div className="form-group">
                        <label>Confirm Password *</label>
                        <input type="password" value={formData.confirmPassword} onChange={(e) => setFormData((p) => ({ ...p, confirmPassword: e.target.value }))} className={formErrors.confirmPassword ? "error" : ""} />
                        {formErrors.confirmPassword && <span className="error-text">{formErrors.confirmPassword}</span>}
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-section">
                  <h4>Dashboard Access Permissions</h4>
                  <p className="section-desc">Select which modules this user can access</p>
                  <div className="permissions-grid">
                    {permissionCategories.map((cat) => (
                      <div key={cat} className="perm-category">
                        <h5>{cat}</h5>
                        {ALL_PERMISSIONS.filter((p) => p.category === cat).map((perm) => (
                          <label key={perm.key} className="perm-checkbox">
                            <input type="checkbox" checked={formData.permissions?.includes(perm.key)} onChange={() => togglePermission(perm.key)} />
                            <span>{perm.label}</span>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowUserModal(false)} disabled={loading}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? <RefreshCw className="spin" size={16} /> : null}
                  {loading ? "Saving..." : modalMode === "create" ? "Create User" : "Update User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && selectedUser && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowPasswordModal(false)}>
          <div className="modal-content">
            <div className="modal-header"><h3><Lock size={20} /> Reset Password</h3><button className="btn-close" onClick={() => setShowPasswordModal(false)}><X size={20} /></button></div>
            <div className="modal-body">
              <p className="reset-info">Resetting password for <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> ({selectedUser.email})</p>
              <div className="form-group">
                <label>New Password *</label>
                <div className="password-input">
                  <input type={showPassword ? "text" : "password"} value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="Enter new password (min 6 characters)" autoFocus />
                  <button type="button" className="btn-eye" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowPasswordModal(false)} disabled={loading}>Cancel</button>
              <button className="btn btn-primary" onClick={handleResetPassword} disabled={loading || !resetPassword}>
                {loading ? <RefreshCw className="spin" size={16} /> : <Key size={16} />}{loading ? "Resetting..." : "Reset Password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedUser && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowDeleteModal(false)}>
          <div className="modal-content modal-sm">
            <div className="modal-header"><h3><Trash2 size={20} /> Delete User</h3><button className="btn-close" onClick={() => setShowDeleteModal(false)}><X size={20} /></button></div>
            <div className="modal-body">
              <div className="delete-warning">
                <XCircle size={48} />
                <p>Are you sure you want to delete <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>?</p>
                <p className="delete-hint">This action cannot be undone. All user data will be permanently removed.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDeleteModal(false)} disabled={loading}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={loading}>
                {loading ? <RefreshCw className="spin" size={16} /> : <Trash2 size={16} />}{loading ? "Deleting..." : "Delete User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowPermissionsModal(false)}>
          <div className="modal-content modal-lg">
            <div className="modal-header"><h3><Shield size={20} /> Manage Permissions</h3><button className="btn-close" onClick={() => setShowPermissionsModal(false)}><X size={20} /></button></div>
            <div className="modal-body">
              <p className="perm-user-info">Managing permissions for <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> ({getRoleLabel(selectedUser.role)})</p>
              <div className="permissions-grid">
                {permissionCategories.map((cat) => (
                  <div key={cat} className="perm-category">
                    <h5>{cat}</h5>
                    {ALL_PERMISSIONS.filter((p) => p.category === cat).map((perm) => (
                      <label key={perm.key} className="perm-checkbox">
                        <input type="checkbox" checked={formData.permissions?.includes(perm.key)} onChange={() => togglePermission(perm.key)} />
                        <span>{perm.label}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowPermissionsModal(false)} disabled={loading}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSavePermissions} disabled={loading}>
                {loading ? <RefreshCw className="spin" size={16} /> : <CheckCircle size={16} />}{loading ? "Saving..." : "Save Permissions"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>    

  )
}
